import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authService from "../../services/authService";
import { getAuthToken, API_BASE } from "../../utils/api";
import type { RegisterDoctorInput } from "../../types/user";

// ---------------------------------------------------------------------------
// Country codes
// ---------------------------------------------------------------------------
const COUNTRY_CODES = [
  { code: "+1", flag: "🇺🇸", label: "US", example: "201-555-5555" },
  { code: "+44", flag: "🇬🇧", label: "GB", example: "7700-900000" },
  { code: "+61", flag: "🇦🇺", label: "AU", example: "412-345-678" },
  { code: "+91", flag: "🇮🇳", label: "IN", example: "98765-43210" },
  { code: "+94", flag: "🇱🇰", label: "LK", example: "71-234-5678" },
  { code: "+1-CA", flag: "🇨🇦", label: "CA", example: "416-555-1234" },
  { code: "+49", flag: "🇩🇪", label: "DE", example: "151-23456789" },
  { code: "+33", flag: "🇫🇷", label: "FR", example: "6-12-34-56-78" },
  { code: "+81", flag: "🇯🇵", label: "JP", example: "90-1234-5678" },
  { code: "+86", flag: "🇨🇳", label: "CN", example: "131-2345-6789" },
  { code: "+971", flag: "🇦🇪", label: "AE", example: "50-123-4567" },
  { code: "+65", flag: "🇸🇬", label: "SG", example: "9123-4567" },
];

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------
const PW_RULES = {
  length: (v: string) => v.length >= 8,
  upper: (v: string) => /[A-Z]/.test(v),
  lower: (v: string) => /[a-z]/.test(v),
  number: (v: string) => /[0-9]/.test(v),
  symbol: (v: string) => /[^A-Za-z0-9]/.test(v),
};

function validateField(
  name: string,
  value: string,
  extra?: { password?: string; countryCode?: string },
): string {
  switch (name) {
    case "email":
      if (!value.trim()) return "Email is required.";
      if (!/^\S+@\S+\.\S+$/.test(value)) return "Enter a valid email address.";
      return "";
    case "first_name":
      return value.trim() ? "" : "First name is required.";
    case "last_name":
      return value.trim() ? "" : "Last name is required.";
    case "phone_number":
      if (!value.trim()) return "Phone number is required.";
      if (!/^\d+$/.test(value.replace(/-/g, "")))
        return "Phone number must contain only digits.";
      return "";
    case "password":
      if (!value) return "Password is required.";
      if (!PW_RULES.length(value)) return "At least 8 characters required.";
      if (!PW_RULES.upper(value)) return "Add at least one uppercase letter.";
      if (!PW_RULES.lower(value)) return "Add at least one lowercase letter.";
      if (!PW_RULES.number(value)) return "Add at least one number.";
      if (!PW_RULES.symbol(value)) return "Add at least one symbol (!@#$...).";
      return "";
    case "confirm_password":
      if (!value) return "Please confirm your password.";
      if (value !== (extra?.password ?? "")) return "Passwords do not match.";
      return "";
    case "address":
      return "";
    case "institution":
      return "";
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Eye icon (inline SVG — no dep needed)
// ---------------------------------------------------------------------------
const EyeIcon = ({ open }: { open: boolean }) =>
  open ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.97 9.97 0 012.19-3.585M9.88 9.88A3 3 0 0114.12 14.12M3 3l18 18"
      />
    </svg>
  );

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RegistrationPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    password: "",
    confirm_password: "",
    address: "",
    institution: "",
  });

  const [countryCode, setCountryCode] = useState(COUNTRY_CODES[0]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (getAuthToken()) navigate("/dashboard");
  }, [navigate]);

  // Live validation on every change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;

      // Phone: only allow digits and dashes
      const sanitized =
        name === "phone_number" ? value.replace(/[^\d-]/g, "") : value;

      setForm((prev) => ({ ...prev, [name]: sanitized }));
      setTouched((prev) => ({ ...prev, [name]: true }));
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(
          name,
          sanitized,
          name === "confirm_password" ? { password: form.password } : undefined,
        ),
      }));

      // Re-validate confirm_password when password changes
      if (name === "password" && touched.confirm_password) {
        setErrors((prev) => ({
          ...prev,
          confirm_password: validateField(
            "confirm_password",
            form.confirm_password,
            {
              password: sanitized,
            },
          ),
        }));
      }
    },
    [form.password, form.confirm_password, touched.confirm_password],
  );

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const found = COUNTRY_CODES.find((c) => c.code === e.target.value);
    if (found) setCountryCode(found);
  };

  const handleEmailBlur = useCallback(async () => {
    const email = form.email.trim();
    if (!email || errors.email) return;
    setEmailChecking(true);
    try {
      const { data } = await axios.get(`${API_BASE}/users/check_email/`, {
        params: { email },
      });
      if (!data.available) {
        setErrors((prev) => ({
          ...prev,
          email: "An account with this email already exists.",
        }));
      }
    } catch {
      // skip silently — server will catch on submit
    } finally {
      setEmailChecking(false);
    }
  }, [form.email, errors.email]);

  // Full validation on submit
  const runFullValidation = (): Record<string, string> => {
    const result: Record<string, string> = {};
    const fields = Object.keys(form) as (keyof typeof form)[];
    for (const f of fields) {
      const err = validateField(
        f,
        form[f],
        f === "confirm_password" ? { password: form.password } : undefined,
      );
      if (err) result[f] = err;
    }
    return result;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAlertMessage("");

    const allErrors = runFullValidation();
    // Mark all as touched
    const allTouched = Object.keys(form).reduce(
      (acc, k) => ({ ...acc, [k]: true }),
      {},
    );
    setTouched(allTouched);
    setErrors(allErrors);

    if (Object.keys(allErrors).length > 0) return;

    // Build full phone number: strip dashes, prefix with country dial code
    const dialCode = countryCode.code.replace("-CA", ""); // +1-CA → +1
    const digits = form.phone_number.replace(/-/g, "");
    const fullPhone = `${dialCode}${digits}`;

    // Derive username from email (local part + random suffix for uniqueness)
    const emailLocal = form.email.split("@")[0].replace(/[^a-zA-Z0-9_]/g, "_");
    const username = `${emailLocal}_${Math.random().toString(36).slice(2, 7)}`;

    const data: RegisterDoctorInput = {
      username,
      password: form.password,
      email: form.email,
      first_name: form.first_name,
      last_name: form.last_name,
      user_type: "doctor",
      phone_number: fullPhone,
      doctor_data: {
        name: `Dr. ${form.first_name} ${form.last_name}`,
        specialization: "",
        bio: "",
        location: form.address,
        hospital: form.institution,
        rating: 4.5,
        availability: [],
      },
    };

    setIsLoading(true);
    try {
      await authService.register(data);
      setSubmitted(true);
    } catch (err: any) {
      if (err && typeof err === "object") {
        const serverErrors: Record<string, string> = {};
        let hasField = false;
        for (const [k, v] of Object.entries(err)) {
          serverErrors[k] = Array.isArray(v) ? v.join(", ") : String(v);
          hasField = true;
        }
        if (hasField) {
          setErrors((prev) => ({ ...prev, ...serverErrors }));
          if (err.non_field_errors || err.error) {
            setAlertMessage(err.non_field_errors?.[0] ?? err.error);
          }
        } else {
          setAlertMessage("Registration failed. Please try again.");
        }
      } else {
        setAlertMessage(
          String(err) || "Registration failed. Please try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Derived password rule states
  const pw = form.password;
  const pwRules = [
    { label: "At least 8 characters", met: PW_RULES.length(pw) },
    { label: "One uppercase letter (A–Z)", met: PW_RULES.upper(pw) },
    { label: "One lowercase letter (a–z)", met: PW_RULES.lower(pw) },
    { label: "One number (0–9)", met: PW_RULES.number(pw) },
    { label: "One symbol (!@#$...)", met: PW_RULES.symbol(pw) },
  ];

  const inputClass = (field: string) =>
    `w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors ${
      touched[field] && errors[field]
        ? "border-red-400 bg-red-50"
        : touched[field] && !errors[field]
          ? "border-green-400"
          : "border-gray-300"
    }`;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-100 px-4 py-8">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 md:p-10 text-center">
          <div className="text-5xl mb-4">📋</div>
          <h2 className="text-2xl font-bold text-blue-700 mb-3">
            Registration Submitted
          </h2>
          <p className="text-gray-600 mb-4">
            Your registration request has been received and is pending review by
            an administrator.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            You will receive an email at <strong>{form.email}</strong> once your
            account has been approved.
          </p>
          <button
            className="text-blue-600 hover:underline font-semibold text-sm"
            onClick={() => navigate("/login")}
            type="button"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-100 px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 md:p-10">
        <h2 className="text-2xl font-bold text-blue-700 mb-2 text-center">
          Doctor Registration
        </h2>
        <p className="text-gray-600 mb-1 text-center">
          Create your medical professional account
        </p>
        <p className="text-sm text-blue-600 mb-5 text-center font-medium">
          For medical professionals only
        </p>

        {alertMessage && (
          <div className="rounded p-2 mb-4 text-center text-sm bg-red-100 text-red-700">
            {alertMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Email */}
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              className={inputClass("email")}
              autoComplete="email"
            />
            {emailChecking && (
              <p className="text-gray-400 text-xs mt-1">
                Checking availability…
              </p>
            )}
            {!emailChecking && touched.email && errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
            {!emailChecking && touched.email && !errors.email && (
              <p className="text-green-600 text-xs mt-1">✓ Looks good</p>
            )}
          </div>

          {/* First / Last Name */}
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="text"
                name="first_name"
                placeholder="First Name"
                value={form.first_name}
                onChange={handleChange}
                className={inputClass("first_name")}
              />
              {touched.first_name && errors.first_name && (
                <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
              )}
            </div>
            <div className="flex-1">
              <input
                type="text"
                name="last_name"
                placeholder="Last Name"
                value={form.last_name}
                onChange={handleChange}
                className={inputClass("last_name")}
              />
              {touched.last_name && errors.last_name && (
                <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
              )}
            </div>
          </div>

          {/* Phone Number with country code selector */}
          <div>
            <div
              className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-400 transition-colors ${
                touched.phone_number && errors.phone_number
                  ? "border-red-400 bg-red-50"
                  : touched.phone_number && !errors.phone_number
                    ? "border-green-400"
                    : "border-gray-300"
              }`}
            >
              {/* Country selector */}
              <select
                value={countryCode.code}
                onChange={handleCountryChange}
                className="bg-gray-100 border-r border-gray-300 px-2 py-2 text-sm focus:outline-none cursor-pointer"
                aria-label="Country code"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code.replace("-CA", "")}
                  </option>
                ))}
              </select>

              {/* Phone input — digits only */}
              <input
                type="tel"
                name="phone_number"
                placeholder={countryCode.example}
                value={form.phone_number}
                onChange={handleChange}
                inputMode="numeric"
                className="flex-1 px-4 py-2 focus:outline-none bg-transparent"
                autoComplete="tel"
              />
            </div>
            {touched.phone_number && errors.phone_number && (
              <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>
            )}
            {touched.phone_number && !errors.phone_number && (
              <p className="text-green-600 text-xs mt-1">✓ Looks good</p>
            )}
          </div>

          {/* Password */}
          <div>
            <div
              className={`flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-400 transition-colors ${
                touched.password && errors.password
                  ? "border-red-400 bg-red-50"
                  : touched.password && !errors.password
                    ? "border-green-400"
                    : "border-gray-300"
              }`}
            >
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={form.password}
                onChange={handleChange}
                className="flex-1 px-4 py-2 focus:outline-none bg-transparent"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="px-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>

            {/* Password requirements list */}
            {(touched.password || form.password.length > 0) && (
              <ul className="mt-2 space-y-1">
                {pwRules.map((r) => (
                  <li
                    key={r.label}
                    className={`text-xs flex items-center gap-1 ${
                      r.met ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    <span>{r.met ? "✓" : "•"}</span>
                    {r.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <div
              className={`flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-400 transition-colors ${
                touched.confirm_password && errors.confirm_password
                  ? "border-red-400 bg-red-50"
                  : touched.confirm_password && !errors.confirm_password
                    ? "border-green-400"
                    : "border-gray-300"
              }`}
            >
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirm_password"
                placeholder="Confirm Password"
                value={form.confirm_password}
                onChange={handleChange}
                className="flex-1 px-4 py-2 focus:outline-none bg-transparent"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="px-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
                tabIndex={-1}
              >
                <EyeIcon open={showConfirmPassword} />
              </button>
            </div>
            {touched.confirm_password && errors.confirm_password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirm_password}
              </p>
            )}
            {touched.confirm_password && !errors.confirm_password && (
              <p className="text-green-600 text-xs mt-1">✓ Passwords match</p>
            )}
          </div>

          {/* Address (renamed from Location) */}
          <div>
            <input
              type="text"
              name="address"
              placeholder="Address"
              value={form.address}
              onChange={handleChange}
              className={inputClass("address")}
            />
          </div>

          {/* Institution (renamed from Hospital) */}
          <div>
            <input
              type="text"
              name="institution"
              placeholder="Institution"
              value={form.institution}
              onChange={handleChange}
              className={inputClass("institution")}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
          >
            {isLoading ? "Registering..." : "Register"}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-gray-600">
          Already have an account?{" "}
          <button
            className="text-blue-600 hover:underline font-semibold"
            onClick={() => navigate("/login")}
            type="button"
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
